import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { IconChevronLeft, IconCamera, IconBluetooth, IconPackageImport } from "@tabler/icons-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { returnsApi } from "../api";
import { focusInputNoScroll } from "../utils/markingScan";
import {
  barIconActiveSx,
  barIconSx,
  camera,
  ds,
  dsText,
  scannerFieldSx,
  sectionLabelSx,
  shellSx,
  toneColor,
  topBarSx,
} from "../theme/keeperTheme";

const SCANNER_STRIP_H = 88;
const CAMERA_H = 200;
const CRPT_DEBOUNCE_CAMERA_MS = 380;
const CRPT_DEBOUNCE_SCANNER_MS = 90;

const ZXING_HINTS = new Map();
ZXING_HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
]);

export default function ReturnAcceptPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const scanControlsRef = useRef(null);
  const scannerInputRef = useRef(null);
  const checkTimerRef = useRef(null);
  const lastProcessedRef = useRef("");

  const [inputMode, setInputMode] = useState("scanner");
  const inputModeRef = useRef(inputMode);
  inputModeRef.current = inputMode;

  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [scannedText, setScannedText] = useState("");
  const [crptLoading, setCrptLoading] = useState(false);
  const [crptError, setCrptError] = useState(null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");
  const [recent, setRecent] = useState([]);

  const loadHistory = useCallback(() => {
    return returnsApi.history(30, 15).then((data) => {
      setRecent(data.recent || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => loadHistory());
  }, [registerToolbarRefresh, loadHistory]);

  useEffect(() => {
    if (inputMode !== "camera") return undefined;
    let stream;
    let cancelled = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) return;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraReady(true);
        setCameraError(null);
      } catch (e) {
        setCameraError(e?.message || "Нет доступа к камере");
        setCameraReady(false);
      }
    })();
    return () => {
      cancelled = true;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setCameraReady(false);
    };
  }, [inputMode]);

  useEffect(() => {
    if (inputMode !== "scanner") return undefined;
    const t = window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 100);
    return () => window.clearTimeout(t);
  }, [inputMode]);

  useEffect(() => {
    if (inputMode !== "camera" || !cameraReady || cameraError) return undefined;
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;
    let cancelled = false;
    const reader = new BrowserMultiFormatReader(ZXING_HINTS);
    reader
      .decodeFromVideoElement(videoEl, (result) => {
        if (!cancelled && result) setScannedText(result.getText());
      })
      .then((controls) => {
        if (!cancelled) scanControlsRef.current = controls;
        else controls.stop();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      scanControlsRef.current?.stop();
      scanControlsRef.current = null;
    };
  }, [inputMode, cameraReady, cameraError]);

  useEffect(() => {
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    const raw = scannedText.trim();
    if (!raw) {
      setCrptError(null);
      setCrptLoading(false);
      return undefined;
    }

    const debounceMs = inputMode === "scanner" ? CRPT_DEBOUNCE_SCANNER_MS : CRPT_DEBOUNCE_CAMERA_MS;
    checkTimerRef.current = setTimeout(() => {
      checkTimerRef.current = null;
      if (lastProcessedRef.current === raw || acceptLoading) return;
      lastProcessedRef.current = raw;
      setAcceptLoading(true);
      setStatusMessage(null);
      setCrptLoading(true);
      setCrptError(null);
      (async () => {
        try {
          const result = await returnsApi.acceptScan({ code: raw, quantity: 1 });
          setStatusTone("success");
          setStatusMessage(
            `+${result.quantity_added} шт. · ${result.product_name} · остаток ${result.stock_quantity}`,
          );
          if (inputModeRef.current === "scanner") {
            setScannedText("");
            if (scannerInputRef.current) scannerInputRef.current.value = "";
            window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
          } else {
            setScannedText("");
          }
          lastProcessedRef.current = "";
          void loadHistory();
        } catch (e) {
          setStatusTone("error");
          setStatusMessage(e?.message || "Ошибка приёма возврата");
          lastProcessedRef.current = "";
        } finally {
          setAcceptLoading(false);
          setCrptLoading(false);
        }
      })();
    }, debounceMs);

    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, [scannedText, inputMode, acceptLoading, loadHistory]);

  const toggleInputMode = () => {
    setInputMode((m) => (m === "camera" ? "scanner" : "camera"));
    setScannedText("");
    setCrptError(null);
    lastProcessedRef.current = "";
    if (scannerInputRef.current) scannerInputRef.current.value = "";
  };

  const handleScannerKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const v = (e.target.value || "").trim();
      e.target.value = "";
      if (v) setScannedText(v);
    }
  };

  const busy = crptLoading || acceptLoading;

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton onClick={() => navigate("/")} sx={barIconSx} aria-label="Назад">
          <IconChevronLeft size={26} />
        </IconButton>
        <IconPackageImport size={22} style={{ color: dsText.info }} />
        <Typography sx={{ color: ds.text, fontWeight: 700, fontSize: "1.0625rem" }}>
          Приём возврат
        </Typography>
      </Box>

      <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, color: ds.text2, fontSize: "0.85rem" }}>
        Отсканируйте маркировку или штрихкод — товар вернётся на остаток склада (+1 шт.).
      </Typography>

      <Box sx={{ px: 2, py: 1, display: "flex", gap: 1 }}>
        <IconButton
          onClick={toggleInputMode}
          sx={inputMode === "scanner" ? barIconActiveSx : barIconSx}
          aria-label="Сканер"
        >
          <IconBluetooth size={22} />
        </IconButton>
        <IconButton
          onClick={toggleInputMode}
          sx={inputMode === "camera" ? barIconActiveSx : barIconSx}
          aria-label="Камера"
        >
          <IconCamera size={22} />
        </IconButton>
      </Box>

      {inputMode === "camera" ? (
        <Box
          sx={{
            height: CAMERA_H,
            position: "relative",
            bgcolor: camera.bg,
            borderRadius: `${ds.radiusLg}px`,
            border: `1px solid ${ds.border2}`,
            overflow: "hidden",
            mx: 2,
          }}
        >
          {cameraError ? (
            <Typography sx={{ color: camera.errorText, p: 2, fontSize: "0.85rem" }}>{cameraError}</Typography>
          ) : (
            <video ref={videoRef} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          )}
        </Box>
      ) : (
        <Box sx={{ px: 2, height: SCANNER_STRIP_H, display: "flex", alignItems: "center" }}>
          <TextField
            inputRef={scannerInputRef}
            fullWidth
            size="small"
            placeholder="Сканируйте сюда…"
            onKeyDown={handleScannerKeyDown}
            autoComplete="off"
            sx={scannerFieldSx}
          />
        </Box>
      )}

      {busy ? (
        <Box sx={{ px: 2, pt: 1 }}>
          <LinearProgress />
        </Box>
      ) : null}

      {statusMessage ? (
        <Typography
          sx={{
            px: 2,
            pt: 1.5,
            color: toneColor(statusTone),
            fontSize: "0.9rem",
            fontWeight: 500,
          }}
        >
          {statusMessage}
        </Typography>
      ) : null}

      {crptError ? (
        <Typography sx={{ px: 2, pt: 1, color: dsText.danger, fontSize: "0.85rem" }}>{crptError}</Typography>
      ) : null}

      <Typography sx={{ ...sectionLabelSx, px: 2, pt: 2, pb: 0.5 }}>
        ПОСЛЕДНИЕ ПРИЁМЫ
      </Typography>
      <List dense sx={{ flex: 1, overflow: "auto", px: 1 }}>
        {recent.length === 0 ? (
          <ListItem>
            <ListItemText primary="Пока нет записей" sx={{ color: ds.text2 }} />
          </ListItem>
        ) : (
          recent.map((row) => (
            <ListItem key={row.id} sx={{ borderBottom: `1px solid ${ds.border}` }}>
              <ListItemText
                primary={row.product_name || `Товар #${row.warehouse_product_id}`}
                secondary={`+${row.quantity} · остаток ${row.stock_after ?? "—"} · ${formatTime(row.created_at)}`}
                primaryTypographyProps={{ color: ds.text, fontSize: "0.9rem", fontWeight: 600 }}
                secondaryTypographyProps={{ color: ds.text2, fontSize: "0.75rem" }}
              />
            </ListItem>
          ))
        )}
      </List>
    </Box>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
